import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Fade,
  Grid,
  Chip,
  Switch,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import MessageIcon from "@mui/icons-material/Message";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import LocalBarIcon from "@mui/icons-material/LocalBar";
import {
  getAllConfigs,
  updateConfig,
  refreshMessageCache,
} from "../../api/configMessages-api";
import { useToast } from "../../components/toaster";

const AdminMessagesConfig = () => {
  const [messages, setMessages] = useState([]);
  const [listLoader, setListLoader] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editFormData, setEditFormData] = useState({
    key: '',
    value: '',
    description: '',
    isActive: true
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [toggleStates, setToggleStates] = useState({});
  const { showToast } = useToast();

  // Enhanced message type configurations with new voucher types
  const messageTypes = {
    shopName: { 
      label: "Shop Name", 
      color: "primary", 
      icon: <MessageIcon />,
      description: "Name and address of the shop where users redeem vouchers" 
    },
    pubName: { 
      label: "Pub Name", 
      color: "secondary", 
      icon: <LocalBarIcon />,
      description: "Name of the pub for final prize redemption" 
    },
    introMsg: { 
      label: "Intro Message", 
      color: "success", 
      icon: <MessageIcon />,
      description: "Welcome message shown when users start the treasure hunt" 
    },
    pauseMsg: { 
      label: "Pause Message", 
      color: "warning", 
      icon: <MessageIcon />,
      description: "Message shown when users reach mid-point and need to visit shop" 
    },
    finalMsg: { 
      label: "Final Message", 
      color: "error", 
      icon: <MessageIcon />,
      description: "Congratulations message when users complete all challenges" 
    },
    welcomeBackMsg: { 
      label: "Welcome Back Message", 
      color: "info", 
      icon: <MessageIcon />,
      description: "Message shown when users return after visiting the shop" 
    },
    giftVocher: { 
      label: "Gift Voucher", 
      color: "warning", 
      icon: <CardGiftcardIcon />,
      description: "Gift voucher image sent to users during the treasure hunt" 
    },
    barVocher: { 
      label: "Bar Voucher", 
      color: "error", 
      icon: <LocalBarIcon />,
      description: "Bar/pub voucher image sent to users as rewards" 
    }
  };

  const fetchMessages = async () => {
    setListLoader(true);
    try {
      const response = await getAllConfigs();
      const fetchedMessages = response?.data?.data || [];
      setMessages(fetchedMessages);
      
      // Initialize toggle states
      const states = {};
      fetchedMessages.forEach(msg => {
        states[msg.key] = msg.isActive ?? true;
      });
      setToggleStates(states);
    } catch (err) {
      showToast('Error fetching message configurations', 'error');
    } finally {
      setListLoader(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const handleEditMessage = (message) => {
    setEditingMessage(message);
    setEditFormData({
      key: message.key || '',
      value: message.value || '',
      description: message.description || '',
      isActive: message.isActive ?? true
    });
    setOpenEditDialog(true);
  };

  const handleToggleActive = async (messageKey, currentStatus) => {
    // Optimistic update
    setToggleStates(prev => ({
      ...prev,
      [messageKey]: !currentStatus
    }));

    try {
      const updateData = {
        isActive: !currentStatus
      };

      await updateConfig(messageKey, updateData);
      showToast(`Message ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      fetchMessages(); // Refresh to get latest data
    } catch (err) {
      // Revert optimistic update on error
      setToggleStates(prev => ({
        ...prev,
        [messageKey]: currentStatus
      }));
      showToast(`Failed to ${!currentStatus ? 'activate' : 'deactivate'} message`, "error");
    }
  };

  const handleUpdateMessage = async () => {
    if (!editFormData.value.trim()) {
      showToast('Message value cannot be empty', 'error');
      return;
    }

    setIsUpdating(true);
    try {
      const updateData = {
        value: editFormData.value,
        description: editFormData.description,
        isActive: editFormData.isActive
      };

      await updateConfig(editingMessage.key, updateData);
      showToast('Message updated successfully');
      fetchMessages();
      setOpenEditDialog(false);
      setEditingMessage(null);
    } catch (err) {
      showToast(`${err.response?.data?.message || "Failed to update message"}`, "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRefreshCache = async () => {
    setIsRefreshing(true);
    try {
      await refreshMessageCache();
      showToast('Message cache refreshed successfully');
    } catch (err) {
      showToast('Failed to refresh message cache', 'error');
    } finally {
      setIsRefreshing(false);
    }
  };

  const formatMessageValue = (value, key) => {
    if (key === 'shopName' || key === 'pubName') {
      return value;
    }
    // Show image indicator for vouchers
    if (key === 'giftvocher' || key === 'barvochers') {
      return value ? `📷 Image: ${value.substring(0, 50)}${value.length > 50 ? '...' : ''}` : 'No image set';
    }
    // Truncate long messages for table display
    return value.length > 100 ? `${value.substring(0, 100)}...` : value;
  };

  const getStatusChip = (isActive) => {
    return (
      <Chip 
        label={isActive ? 'Active' : 'Inactive'} 
        size="small"
        color={isActive ? 'success' : 'default'}
        variant={isActive ? 'filled' : 'outlined'}
        sx={{ 
          minWidth: 70,
          fontWeight: 600,
          ...(isActive ? {} : { opacity: 0.7 })
        }}
      />
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <MessageIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="bold">
            Messages Configuration
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={isRefreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
          onClick={handleRefreshCache}
          disabled={isRefreshing}
        >
          Refresh Cache
        </Button>
      </Box>

      {/* Alert Info */}
      <Fade in={true}>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Manage messages and vouchers for the treasure hunt experience. 
            Use the toggle to activate/deactivate messages, and edit button to modify content.
          </Typography>
        </Alert>
      </Fade>

      {/* Messages Table */}
      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Message Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Current Value</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Last Updated</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {listLoader ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Loading message configurations...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : messages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No message configurations found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                messages.map((message) => {
                  const messageType = messageTypes[message.key] || { 
                    label: message.key, 
                    color: "default", 
                    icon: <MessageIcon />,
                    description: "" 
                  };
                  const isActive = toggleStates[message.key] ?? (message.isActive ?? true);
                  
                  return (
                    <TableRow 
                      key={message._id} 
                      sx={{ 
                        '&:hover': { backgroundColor: 'grey.50' },
                        borderLeft: `4px solid transparent`,
                        opacity: isActive ? 1 : 0.6
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ color: `${messageType.color}.main` }}>
                            {messageType.icon}
                          </Box>
                          <Chip 
                            label={messageType.label} 
                            color={messageType.color} 
                            size="small" 
                            variant="outlined"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getStatusChip(isActive)}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            wordBreak: 'break-word',
                            fontFamily: 'monospace',
                            backgroundColor: isActive ? 'grey.100' : 'grey.200',
                            p: 1,
                            borderRadius: 1,
                            fontSize: '0.85rem',
                            opacity: isActive ? 1 : 0.7
                          }}
                        >
                          {formatMessageValue(message.value, message.key)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography variant="body2" color="text.secondary">
                          {message.description || messageType.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {message.updatedAt ? new Date(message.updatedAt).toLocaleDateString() : '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          by {message.updatedBy || 'system'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                          {/* Toggle Switch */}
                          <Tooltip title={isActive ? 'Click to deactivate' : 'Click to activate'}>
                            <Switch
                              checked={isActive}
                              onChange={() => handleToggleActive(message.key, isActive)}
                              size="small"
                              color="primary"
                            />
                          </Tooltip>
                          
                          {/* Edit Button */}
                          <Tooltip title="Edit message">
                            <IconButton
                              onClick={() => handleEditMessage(message)}
                              color="primary"
                              size="small"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Enhanced Edit Dialog */}
      <Dialog 
        open={openEditDialog} 
        onClose={() => setOpenEditDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { 
            minHeight: '70vh', 
            maxWidth: '900px',
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        {/* Enhanced Dialog Header */}
        <DialogTitle 
          sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            py: 3,
            position: 'relative'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box 
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.2)', 
                borderRadius: '50%', 
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <EditIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight="600">
                Edit Message Configuration
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                {messageTypes[editFormData.key]?.label || editFormData.key}
              </Typography>
            </Box>
          </Box>
          
          {/* Message Type Chip */}
          <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
            <Chip 
              label={messageTypes[editFormData.key]?.label || editFormData.key}
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 'bold'
              }}
            />
          </Box>
        </DialogTitle>

        {/* Enhanced Dialog Content */}
        <DialogContent sx={{ p: 0, backgroundColor: '#f8fafc' }}>
          <Box sx={{ p: 4 }}>
            {/* Message Type Info Card */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                mb: 4, 
                backgroundColor: '#e3f2fd', 
                border: '1px solid #bbdefb'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                <Box 
                  sx={{ 
                    backgroundColor: '#2196f3', 
                    borderRadius: '50%', 
                    p: 0.5,
                    mt: 0.5
                  }}
                >
                  <MessageIcon sx={{ fontSize: 16, color: 'white' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight="600" color="primary.dark">
                    Message Purpose
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {messageTypes[editFormData.key]?.description || 'Custom message configuration'}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Form Fields */}
            <Grid container spacing={4}>
              {/* Active Status Toggle */}
              <Grid item xs={12}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 3, 
                    backgroundColor: editFormData.isActive ? '#e8f5e8' : '#fafafa', 
                    border: `1px solid ${editFormData.isActive ? '#c8e6c9' : '#e0e0e0'}`
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h6" fontWeight="600" color="text.primary">
                        Message Status
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Control whether this message is active and will be displayed to users
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {getStatusChip(editFormData.isActive)}
                      <Switch
                        checked={editFormData.isActive}
                        onChange={(e) => setEditFormData({ ...editFormData, isActive: e.target.checked })}
                        color="primary"
                      />
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Message Value Section */}
              <Grid item xs={12}>
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" fontWeight="600" color="text.primary">
                      Message Content
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {['giftvocher', 'barvochers'].includes(editFormData.key) 
                        ? 'Image path or base64 data for the voucher'
                        : 'The actual message that users will see in the application'
                      }
                    </Typography>
                  </Box>
                  
                  <TextField
                    label={['giftvocher', 'barvochers'].includes(editFormData.key) ? 'Image Data/Path' : 'Message Value'}
                    multiline
                    rows={8}
                    fullWidth
                    required
                    value={editFormData.value}
                    onChange={(e) => setEditFormData({ ...editFormData, value: e.target.value })}
                    placeholder={['giftvocher', 'barvochers'].includes(editFormData.key) 
                      ? 'Enter image path or base64 data...'
                      : 'Enter the message content that users will see...'
                    }
                    variant="outlined"
                    sx={{
                      width: '100%',
                      '& .MuiInputBase-root': {
                        fontSize: '15px',
                        lineHeight: 1.6,
                        fontFamily: '"Inter", "Roboto", sans-serif'
                      },
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        '&:hover fieldset': {
                          borderColor: '#667eea',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#667eea',
                          borderWidth: '2px'
                        }
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <Box sx={{ 
                          position: 'absolute', 
                          bottom: 8, 
                          right: 12,
                          fontSize: '12px',
                          color: 'text.secondary'
                        }}>
                          {editFormData.value.length} characters
                        </Box>
                      )
                    }}
                  />
                </Box>
              </Grid>

              {/* Description Section */}
              <Grid item xs={12}>
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" fontWeight="600" color="text.primary">
                      Description (Optional)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Additional notes or context about this message
                    </Typography>
                  </Box>
                  
                  <TextField
                    label="Description"
                    multiline
                    rows={3}
                    fullWidth
                    value={editFormData.description}
                    onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                    placeholder="Optional description for administrative purposes..."
                    variant="outlined"
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white'
                      }
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        {/* Enhanced Dialog Actions */}
        <DialogActions 
          sx={{ 
            p: 3, 
            backgroundColor: 'white',
            borderTop: '1px solid #e2e8f0',
            gap: 2
          }}
        >
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {/* Status indicator */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box 
                sx={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  backgroundColor: editFormData.value.trim() && editFormData.isActive ? '#10b981' : 
                    editFormData.value.trim() && !editFormData.isActive ? '#f59e0b' : '#ef4444'
                }} 
              />
              <Typography variant="caption" color="text.secondary">
                {editFormData.value.trim() 
                  ? editFormData.isActive ? 'Active & Ready' : 'Inactive but Ready'
                  : 'Message cannot be empty'
                }
              </Typography>
            </Box>

            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                onClick={() => setOpenEditDialog(false)} 
                disabled={isUpdating}
                size="large"
                sx={{ 
                  minWidth: 100,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateMessage}
                variant="contained"
                startIcon={isUpdating ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                disabled={isUpdating || !editFormData.value.trim()}
                size="large"
                sx={{ 
                  minWidth: 160,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5A67D8 0%, #6B46C1 100%)',
                    boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)',
                  },
                  '&:disabled': {
                    background: '#e2e8f0',
                    color: '#94a3b8'
                  }
                }}
              >
                {isUpdating ? 'Updating...' : 'Update Message'}
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default AdminMessagesConfig;